import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Stack, Pagination, Box, Button, TableSortLabel } from "@mui/material";
import _ from "lodash";
import moment from 'moment';
// import { useTranslation } from "react-i18next";
import { useEffect, useState, useMemo } from "react";
import { CSVLink } from "react-csv";

const formatDate = function(column: any, alert: any) {
    const isoDate: string = _.get(alert, column.path);
    return moment(isoDate).format("DD/MM/YYYY HH:00");
}

const formatDuration = function(column: any, alert: any) {
    const duration = _.get(alert, column.path);
    return `${duration}H`;
}

const renderDetails = function(column: any, alert: any) {
    switch(alert.properties.type) {
        case "THUNDER":
            return `Grêle : ${alert.properties.hail ? "Oui" : "Non"}`;
        default:
            return null
    }
}

const columns = [{
    label: "Code Postal",
    path: "properties.postal_code",
    sortable: true
}, {
    label: "Type",
    path: "properties.type",
    sortable: true
}, {
    label: "Debut",
    path: "properties.expected_at",
    transform: formatDate,
    sortable: true
}, {
    label: "Fin",
    path: "properties.estimated_end",
    transform: formatDate,
    sortable: true
}, {
    label: "Durée",
    path: "properties.risk_duration",
    transform: formatDuration,
    sortable: true
}, {
    label: "Niveau",
    path: "properties.level",
    sortable: true
}, {
    label: "Détails",
    transform: renderDetails,
    sortable: false
}];

const renderColumnValue = function(column: any, alert: any) {
    if (column.transform) {
        return column.transform(column, alert);
    }

    return _.get(alert, column.path);
}

const selectAlertPage = function(alerts: any[], page: number, itemsPerPage: number) {
    if (!alerts || alerts.length === 0) { return []; }
    return alerts.slice((page - 1) * itemsPerPage, page * itemsPerPage);
}

const sortAlerts = function(alerts: any[], sortField: string, sortDirection: 'asc' | 'desc') {
    if (!alerts || alerts.length === 0) { return alerts; }
    
    return [...alerts].sort(function(a: any, b: any) {
        let aValue = _.get(a, sortField);
        let bValue = _.get(b, sortField);
        
        // Handle date sorting
        if (sortField === 'properties.expected_at' || sortField === 'properties.estimated_end') {
            aValue = moment(aValue).valueOf();
            bValue = moment(bValue).valueOf();
        }
        
        // Handle numeric sorting for duration
        if (sortField === 'properties.risk_duration') {
            aValue = parseInt(aValue) || 0;
            bValue = parseInt(bValue) || 0;
        }
        
        // Handle string sorting
        if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) {
            return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
    });
}

const itemsPerPage = 20;

export default function AlertTable({ data = {features: []}, onAlertSelect }: {data: any, onAlertSelect?: (alert: any) => void}) {
    const [sortField, setSortField] = useState<string>('properties.expected_at');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [selectedRow, setSelectedRow] = useState<number | null>(null);
    
    // Use the already filtered data directly - no need to filter again
    // Create a stable key to prevent unnecessary recalculations
    const dataKey = useMemo(function() {
        const features = data?.features || [];
        if (features.length === 0) return 'empty';
        // Create a stable key based on features length and a hash of feature IDs
        const ids = features.map((f: any) => f.properties?.id || f.id || '').join(',');
        return `${features.length}-${ids.substring(0, 100)}`; // Limit length for performance
    }, [data?.features]);
    
    const filteredAlerts = useMemo(function() {
        const features = data?.features || [];
        return sortAlerts(features, sortField, sortDirection);
    }, [dataKey, sortField, sortDirection]);
    
    const alertCount = filteredAlerts.length;

    const [alerts, setAlerts] = useState<any>([]);
    // const { t } = useTranslation();

    useEffect(function() {
        setAlerts(selectAlertPage(filteredAlerts, 1, itemsPerPage));
    }, [filteredAlerts]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setAlerts(selectAlertPage(filteredAlerts, value, itemsPerPage));
    };

    const handleSort = (field: string) => {
        const isAsc = sortField === field && sortDirection === 'asc';
        setSortDirection(isAsc ? 'desc' : 'asc');
        setSortField(field);
    };

    const handleRowClick = (alert: any, rowIndex: number) => {
        setSelectedRow(rowIndex);
        if (onAlertSelect) {
            onAlertSelect([alert.properties]);
        }
    };

    const dataToCsv = useMemo(function() {
        return filteredAlerts.map(function(alert: any) {
            return columns.reduce(function(acc: any, column: any) {
                acc[column.label] = renderColumnValue(column, alert);
                return acc;
            }, {});
        })
    }, [filteredAlerts]);

    return (
        <Box style={{height: "100%", display: "flex", flexDirection: "column"}}>
            <TableContainer sx={{ 
                paddingLeft: 2, 
                paddingRight: 2, 
                paddingTop: 0, 
                backgroundColor: "white",
                flex: 1,
                overflow: "auto"
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {
                                columns.map(function(column: any, cellIndex: number) {
                                    return (
                                        <TableCell key={`header-row-cell-${cellIndex}`}>
                                            {column.sortable ? (
                                                <TableSortLabel
                                                    active={sortField === column.path}
                                                    direction={sortField === column.path ? sortDirection : 'asc'}
                                                    onClick={() => handleSort(column.path)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            ) : (
                                                column.label
                                            )}
                                        </TableCell>
                                    )
                                })
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                    {
                        alerts && alerts.map(function(alert: any, lineIndex: number) {
                            return (
                                <TableRow 
                                    key={`row-${lineIndex}`} 
                                    onClick={() => handleRowClick(alert, lineIndex)}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: selectedRow === lineIndex ? '#e3f2fd' : 'inherit',
                                        '&:hover': {
                                            backgroundColor: selectedRow === lineIndex ? '#bbdefb' : '#f5f5f5',
                                        }
                                    }}
                                >
                                    {
                                        columns.map(function(column: any, cellIndex: number) {
                                            return (
                                                <TableCell key={`line-${lineIndex}-cell-${cellIndex}`}>{renderColumnValue(column, alert)}</TableCell>
                                            )
                                        })
                                    }
                                </TableRow>
                            )
                        })
                    }
                    </TableBody>
                </Table>
            </TableContainer>
            
            {/* Sticky footer with pagination, alert count and export button */}
            <Box sx={{
                display: "flex", 
                flexDirection: "column",
                paddingX: 8, 
                paddingY: 2,
                backgroundColor: "white",
                position: "sticky",
                bottom: 0,
                zIndex: 1
            }}>
                {/* Alert count, pagination and export button in one row */}
                <Box sx={{display: "flex", alignItems: "center", width: "100%"}}>
                    <Box sx={{flex: 1, display: "flex", justifyContent: "flex-start"}}>
                        <p>
                            <span>{alertCount} Alertes</span>
                        </p>
                    </Box>
                    <Box sx={{flex: 1, display: "flex", justifyContent: "center"}}>
                        <Pagination
                            count={Math.ceil(alertCount / itemsPerPage)}
                            onChange={handlePageChange}
                            size="small"
                        />
                    </Box>
                    <Box sx={{flex: 1, display: "flex", justifyContent: "flex-end"}}>
                        <CSVLink filename="data.csv" data={dataToCsv} headers={columns.map(function(column: any) { return column.label; })}>
                            <Button variant="contained">Exporter</Button>
                        </CSVLink>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}